import React, { useState } from 'react';
import type { SortableItemState } from 'admin/components/common/SortableList';
import PanelTooltip from 'admin/components/pages/Story/PanelTooltip';
import { usePopoverContext } from '../Popover';
import { LabelWithTooltip } from '../LabelWithTooltip';
import RowAction from '../PropField/RowAction';
import { Label } from '../PropField/Label';
import type { getInheritanceInfo } from '../utils/get-inheritance-info';
import type { FieldConfig } from '../types';
import css from './LayerRows.scss';

export { moveLayer } from './move-layer';

/** The block's styles, for hosts that lay out their own rows */
export { css as layerRowsCss };

export type InheritanceType = ReturnType<typeof getInheritanceInfo>['type'];

export type AddItem = {
	label: string;
	thumb: React.ReactNode;
	/** Items with the same group follow one heading, e.g. "Presets" */
	group?: string;
	/** Shown at the item's end, e.g. "2 layers" */
	meta?: string;
} & (
	| { add: () => void }
	/** Media opens its editor first; the item adds nothing until an image or video is chosen there */
	| { editor: (close: () => void) => React.ReactNode }
);

export type ActionItem = {
	label: string;
	icon: React.ReactNode;
	onSelect: () => void;
	/** Why the action is unavailable now; the item shows dimmed, with this in its tooltip */
	disabledReason?: string;
	/** Starts another set of actions below a divider, such as the ones that remove a preset */
	divided?: boolean;
};

/** A menu of commands in an Inspector popover, such as a preset's ⋯: the Add layer menu's rows and surface */
export function ActionMenu({ items }: { items: ActionItem[] }) {
	const popover = usePopoverContext();
	return (
		<div className={css.menu} role="menu">
			{items.map(item => {
				const button = (
					<button
						type="button"
						role="menuitem"
						className={css.menuItem}
						disabled={!!item.disabledReason}
						onClick={() => {
							popover?.set(false);
							item.onSelect();
						}}
					>
						{item.icon}
						{item.label}
					</button>
				);
				return (
					<React.Fragment key={item.label}>
						{item.divided ? <span className={css.menuDivider} role="separator" /> : null}
						{item.disabledReason ? (
							// A disabled button gets no pointer events; the slot carries its tooltip
							<PanelTooltip content={item.disabledReason} placement="left">
								<span className={css.menuSlot}>{button}</span>
							</PanelTooltip>
						) : (
							button
						)}
					</React.Fragment>
				);
			})}
		</div>
	);
}

/** The Add layer popover: a menu, then the chosen item's editor when it needs media first */
export function AddMenu({ items }: { items: AddItem[] }) {
	const popover = usePopoverContext();
	return <AddMenuList items={items} onClose={() => popover?.set(false)} />;
}

/** The Add layer menu in any popover; `onClose` closes that popover once an item is added */
export function AddMenuList({ items, onClose }: { items: AddItem[]; onClose: () => void }) {
	const [editing, setEditing] = useState<AddItem | null>(null);
	const close = () => setTimeout(onClose);
	if (editing && 'editor' in editing) return <>{editing.editor(close)}</>;

	return (
		<div className={css.menu}>
			{items.map((item, index) => (
				<React.Fragment key={item.label}>
					{item.group && item.group !== items[index - 1]?.group ? (
						<span className={css.menuHeading}>{item.group}</span>
					) : null}
					<button
						type="button"
						className={css.menuItem}
						onClick={() => {
							if ('add' in item) {
								item.add();
								close();
							} else setEditing(item);
						}}
					>
						{item.thumb}
						{item.label}
						{item.meta ? <small>{item.meta}</small> : null}
					</button>
				</React.Fragment>
			))}
		</div>
	);
}

/** Shown while its row is pointed at; the row itself opens the row's editor */
export function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
	return (
		<RowAction
			action="remove"
			label={label}
			className={css.remove}
			onClick={event => {
				event.stopPropagation();
				onClick();
			}}
		/>
	);
}

/**
 * A row's label, as every field's: what the row is (Tint, Gradient, Drop, Inner), and on hover or right-click the
 * value's platform, state and Reset.
 */
export function RowLabel(props: { field?: FieldConfig; children: string }) {
	return (
		// The tooltip renders in a portal; its clicks still bubble here and must not open the row's editor
		<span role="presentation" className={css.label} onClick={event => event.stopPropagation()}>
			<Label component="span">
				{props.field ? (
					<LabelWithTooltip field={props.field}>{props.children}</LabelWithTooltip>
				) : (
					props.children
				)}
			</Label>
		</span>
	);
}

/** A row's value at the row end, in the field colors (assigned, inherited, default); the preview closes it */
export function RowValue(props: { inheritance: InheritanceType; children: React.ReactNode }) {
	return (
		<span className={css.end} data-inheritance={props.inheritance}>
			{props.children}
		</span>
	);
}

/** The target of the tooltip's Reset, which clicks the field's hidden input as for any Inspector field */
export function ResetInput({ field, onReset }: { field?: FieldConfig; onReset: () => void }) {
	return field ? <input type="hidden" name={field.path} onClick={onReset} /> : null;
}

/** The only drag handle of a sortable row: it opens after the preview on hover */
export function RowGrip(props: { label: string; handle: SortableItemState['handle'] }) {
	const { label, handle } = props;
	return (
		<span className={css.grip} {...handle} aria-label={label}>
			<svg width="6" height="10" viewBox="0 0 6 10" fill="currentColor" aria-hidden="true">
				<circle cx="1.5" cy="1.5" r="1" />
				<circle cx="4.5" cy="1.5" r="1" />
				<circle cx="1.5" cy="5" r="1" />
				<circle cx="4.5" cy="5" r="1" />
				<circle cx="1.5" cy="8.5" r="1" />
				<circle cx="4.5" cy="8.5" r="1" />
			</svg>
		</span>
	);
}
